import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Eye, EyeOff } from "lucide-react";
import { useAuthToken } from "../../hooks/user/useAuthToken";
import { ACCESS_TOKEN, REFRESH_TOKEN } from "../../constants";

import emailIcon from "../../Assets/email.png";
import passwordIcon from "../../Assets/password.png";
import backgroundLogin from "../../Assets/backgroundlogin.jpg";
import logoNew from "../../Assets/logo_new.jpeg";
import "./Login.css";
import type { User as BackendUser } from "../../types/user";

type LoginProps = {
  onLogin?: (username: string, password: string) => Promise<BackendUser>;
  user?: BackendUser | null;
};

export default function Login({ onLogin, user }: LoginProps) {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const auth = useAuthToken();

  const handleSuccessNavigation = (loggedInUser: BackendUser) => {
    // Redirect based on user role
    const userRole = loggedInUser?.role?.toLowerCase();

    if (userRole === "admin") {
      navigate("/dashboard", { replace: true });
    } else if (userRole === "user" || userRole === "superuser") {
      navigate("/data-entry/canevas", { replace: true });
    } else if (userRole === "agent") {
      navigate("/data-entry/validation", { replace: true });
    } else {
      // Default fallback
      navigate("/dashboard", { replace: true });
    }
  };

  const handleLocalLogin = async (id: string, pwd: string) => {
    const data = await auth.mutateAsync({ user: id, password: pwd });
    const access = data.access ?? data.token ?? data.access_token;
    const refresh = data.refresh ?? data.refresh_token;

    if (access) localStorage.setItem(ACCESS_TOKEN ?? "authToken", access);
    if (refresh) localStorage.setItem(REFRESH_TOKEN ?? "refreshToken", refresh);

    await qc.invalidateQueries({ queryKey: ["current-user"] });
    await qc.refetchQueries({ queryKey: ["current-user"] });
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      let loggedInUser: BackendUser | undefined;

      if (onLogin) {
        loggedInUser = await onLogin(identifier, password);
      } else {
        await handleLocalLogin(identifier, password);
        // For local login, we need to refetch to get user data
        const userQuery = await qc.fetchQuery({ queryKey: ["current-user"] });
        loggedInUser = userQuery as BackendUser;
      }

      if (loggedInUser) {
        handleSuccessNavigation(loggedInUser);
      }
    } catch (err: any) {
      console.error("Login failed:", err);
      setError(err?.message ?? "Échec de la connexion");
    }
  };

  const handleLogin = () => {
    const form = document.createElement("form");
    const event = new Event("submit", { bubbles: true, cancelable: true });
    form.addEventListener("submit", onSubmit as unknown as EventListener);
    form.dispatchEvent(event);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleLogin();
    }
  };

  return (
    <div
      className="login-wrapper"
      style={{
        backgroundImage: `url(${backgroundLogin})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="container">
        <div className="login-brand">
          <img src={logoNew} alt="Yameera" className="login-logo" />
          <div className="login-brand-name">Yameera</div>
        </div>
        <div className="underline"></div>

        {error && (
          <div className="error" style={{ color: "#b91c1c", fontSize: 13, marginBottom: 10 }}>
            {error}
          </div>
        )}

        <div className="inputs">
          <div className="input">
            <img src={emailIcon} alt="email icon" />
            <input
              type="email"
              placeholder="Email"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              onKeyPress={handleKeyPress}
            />
          </div>

          <div className="input">
            <img src={passwordIcon} alt="password icon" />
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={handleKeyPress}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "0 10px",
                color: "#666",
                display: "flex",
                alignItems: "center",
              }}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <Eye size={20} /> : <EyeOff size={20} />}
            </button>
          </div>
        </div>

        <div className="forgot-password">
          Mot de passe oublié? <span>Cliquez ici</span>
        </div>

        <div className="submit-container">
          <div className="submit" onClick={handleLogin}>
            Se connecter
          </div>
        </div>
      </div>
    </div>
  );
}
